/*
    Tidy whitespace at inline formatting boundaries in Quill HTML.

    Per <p>:
      - collapse runs of spaces to one space (across tags) and trim the line edges
      - move spaces at the start/end of an inline element outside of it
      - merge adjacent identical inline elements, including across a single space
        (<em>by a</em> <em>number</em> -> <em>by a number</em>)
      - drop empty inline elements and an empty class attribute

    Visible text and per-word formatting never change, only redundant markup and whitespace.
    &nbsp; is left alone because it may be intentional.

    Accepts an HTML string (needs DOMParser) or a <p> element (normalised in place).
*/

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const MERGEABLE = new Set(["EM", "STRONG", "U", "I", "B", "S", "SPAN", "SUB", "SUP"]);
function isMergeable(node) {
  return node && node.nodeType === ELEMENT_NODE && MERGEABLE.has(node.nodeName);
}
function formatKey(element) {
  const attributes = Array.from(element.attributes || []).map(attr => `${attr.name}=${attr.value.replace(/\s+/g, "").replace(/;$/, "")}`).sort();
  return `${element.nodeName}|${attributes.join("|")}`;
}
function collectTextNodes(root) {
  const nodes = [];
  const walk = node => {
    if (node.nodeType === TEXT_NODE) {
      nodes.push(node);
    } else if (node.nodeName !== "RT") {
      node.childNodes.forEach(walk);
    }
  };
  walk(root);
  return nodes;
}
function collapseSpaces(paragraph) {
  const nodes = collectTextNodes(paragraph);
  let lastWasSpace = true;
  for (const node of nodes) {
    let out = "";
    for (const ch of node.data) {
      if (ch === " " && lastWasSpace) {
        continue;
      }
      out += ch;
      lastWasSpace = ch === " ";
    }
    if (out !== node.data) {
      node.data = out;
    }
  }
  for (let i = nodes.length - 1; i >= 0; i--) {
    const trimmed = nodes[i].data.replace(/ +$/, "");
    if (trimmed !== nodes[i].data) {
      nodes[i].data = trimmed;
    }
    if (trimmed.length > 0) {
      break;
    }
  }
}
function hoistEdgeSpaces(element) {
  Array.from(element.childNodes).forEach(child => {
    if (child.nodeType === ELEMENT_NODE) {
      hoistEdgeSpaces(child);
    }
  });
  if (!isMergeable(element)) {
    return;
  }
  const doc = element.ownerDocument;
  const first = element.firstChild;
  if (first && first.nodeType === TEXT_NODE && first.data.startsWith(" ")) {
    first.data = first.data.slice(1);
    element.parentNode.insertBefore(doc.createTextNode(" "), element);
  }
  const last = element.lastChild;
  if (last && last.nodeType === TEXT_NODE && last.data.endsWith(" ")) {
    last.data = last.data.slice(0, -1);
    element.parentNode.insertBefore(doc.createTextNode(" "), element.nextSibling);
  }
}
function mergeSiblings(parent) {
  let node = parent.firstChild;
  while (node) {
    if (isMergeable(node)) {
      let next = node.nextSibling;
      let separator = null;
      if (next && next.nodeType === TEXT_NODE && /^ ?$/.test(next.data) && isMergeable(next.nextSibling)) {
        separator = next;
        next = next.nextSibling;
      }
      if (isMergeable(next) && formatKey(next) === formatKey(node)) {
        if (separator) {
          node.appendChild(separator);
        }
        while (next.firstChild) {
          node.appendChild(next.firstChild);
        }
        next.remove();
        continue;
      }
    }
    node = node.nextSibling;
  }
  Array.from(parent.childNodes).forEach(child => {
    if (child.nodeType === ELEMENT_NODE) {
      mergeSiblings(child);
    }
  });
}
function removeEmpty(element) {
  Array.from(element.childNodes).forEach(child => {
    if (child.nodeType === ELEMENT_NODE) {
      removeEmpty(child);
      if (isMergeable(child) && !child.textContent && !child.querySelector("br, img")) {
        child.remove();
      }
    } else if (child.nodeType === TEXT_NODE && child.data === "") {
      child.remove();
    }
  });
}
function normalizeParagraph(paragraph) {
  collapseSpaces(paragraph);
  hoistEdgeSpaces(paragraph);
  collapseSpaces(paragraph);
  mergeSiblings(paragraph);
  removeEmpty(paragraph);
  paragraph.normalize();
  if (paragraph.getAttribute && paragraph.getAttribute("class") === "") {
    paragraph.removeAttribute("class");
  }
  return paragraph;
}
export default (function normalizeInlineWhitespace(input) {
  if (!input) {
    return input;
  }
  if (typeof input !== "string") {
    return normalizeParagraph(input);
  }
  if (typeof DOMParser === "undefined" || !/<p\b/i.test(input)) {
    return input;
  }
  const doc = new DOMParser().parseFromString(input, "text/html");
  const paragraphs = Array.from(doc.body.getElementsByTagName("p"));
  paragraphs.forEach(normalizeParagraph);
  return paragraphs.map(p => p.outerHTML).join("");
});